// The plan's home page, in two states.
//
// Nothing started: three ways in (a text box that goes into the first conversation, the
// planning-topics catalog, the first conversation itself). Something started: the most recent
// unfinished work, the other issues, and links onward. The featured item is the newest of the
// viewer's issues (by last acted, else created) and the topics they have started and not finished
// that have no issue; a topic with an issue is never featured. Nothing started means no issue and
// no started topic; with no featurable item the featured card is left out.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { Decision } from '../api/contract.js';
import { topicPhrase } from '../format.js';
import { useOpenSuggestion } from '../plan/openSuggestion.js';
import { ownThreadOf, viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { FacilitatorState } from './FacilitatorState.js';

export type HomePageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
};

// The text box's send, in two steps (see `talkItThrough`). `known` is the ids of the suggestions
// on the page before the send, so a suggestion Mor wrote in answer is told from an old one.
// `reply`: waiting for a facilitator message after `seq`. `decide`: that message has been seen
// and one refetch asked for; the first snapshot other than `after` is read once.
type PendingSend =
  | { phase: 'reply'; seq: string; known: readonly string[] }
  | {
      phase: 'decide';
      known: readonly string[];
      after: NonNullable<PlanState['snapshot']>;
    };

type Featured =
  | { kind: 'issue'; at: string; issue: Decision }
  | { kind: 'topic'; at: string; key: string };

export function HomePage({ state, client, planId }: HomePageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingSend | null>(null);
  const opener = useOpenSuggestion({ client, planId, state, navigate });

  useEffect(() => {
    if (pending === null || snapshot === null) {
      return;
    }
    if (pending.phase === 'reply') {
      const replied = snapshot.messages.some(
        (message) =>
          message.type === 'facilitator' && BigInt(message.seq) > BigInt(pending.seq),
      );
      if (replied) {
        setPending({ phase: 'decide', known: pending.known, after: snapshot });
        state.refetch();
      }
      return;
    }
    if (snapshot === pending.after) {
      return;
    }
    // Cleared before anything else, so this branch cannot run a second time.
    setPending(null);
    const suggested = [...(snapshot.plan.firstConversation?.suggestions ?? [])]
      .filter(
        (suggestion) =>
          suggestion.kind === 'issue' &&
          !suggestion.opened &&
          !pending.known.includes(suggestion.id),
      )
      .sort((a, b) => a.rank - b.rank)[0];
    if (suggested === undefined) {
      navigate(`/plans/${planId}/start`);
    } else {
      void opener.open(suggested, { from: 'home' });
    }
  }, [pending, snapshot]);

  if (snapshot === null) {
    return null;
  }
  const isCreator = snapshot.plan.viewerIsCreator;
  const principal = viewerIsPrincipal(snapshot);
  const phraseOf = (key: string): string => {
    const topic = snapshot.topics.find((candidate) => candidate.key === key);
    return topic === undefined ? '' : topicPhrase(topic);
  };

  const issues = snapshot.decisions;
  const candidates: Featured[] = [];
  for (const issue of issues) {
    candidates.push({
      kind: 'issue',
      at: issue.lastActedAt ?? issue.createdAt ?? '',
      issue,
    });
  }
  for (const topic of snapshot.topics) {
    const hasIssue = issues.some((issue) => issue.topic === topic.key);
    if (!topic.viewer.started || topic.viewer.finished || hasIssue) {
      continue;
    }
    const stamps = [topic.viewer.lastWroteAt, topic.viewer.startedAt]
      .filter((stamp): stamp is string => stamp !== null)
      .sort();
    candidates.push({
      kind: 'topic',
      at: stamps[stamps.length - 1] ?? '',
      key: topic.key,
    });
  }
  candidates.sort((a, b) => b.at.localeCompare(a.at));
  const featured = candidates[0];
  const nothingStarted =
    issues.length === 0 && !snapshot.topics.some((topic) => topic.viewer.started);

  const otherIssues = issues
    .filter((issue) => !(featured?.kind === 'issue' && featured.issue.id === issue.id))
    .sort(
      (a, b) =>
        (b.lastActedAt ?? '').localeCompare(a.lastActedAt ?? '') ||
        (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );

  const ownPlanThread = ownThreadOf(snapshot.threads, null);
  const talkItThrough = (): void => {
    const body = text.trim();
    if (body === '' || ownPlanThread === null || sending || pending !== null) {
      return;
    }
    const known = (snapshot.plan.firstConversation?.suggestions ?? []).map(
      (suggestion) => suggestion.id,
    );
    setSending(true);
    setProblem(null);
    state.post(body).then(
      (stored) => {
        setSending(false);
        setPending({ phase: 'reply', seq: stored.seq, known });
      },
      () => {
        setSending(false);
        setProblem('That didn’t send. Try again.');
      },
    );
  };
  const sent = sending || pending !== null;
  const waiting = sending || pending !== null || opener.opening !== null;
  const shownProblem = problem ?? opener.problem;

  const aboutPlan = (withPeople: boolean): JSX.Element => (
    <section className="home__section">
      <h3 className="home__heading">About this plan</h3>
      <div className="home__areaLinks">
        {withPeople ? (
          <Link className="home__areaLink" to={`/plans/${planId}/people`}>
            People<span aria-hidden="true">→</span>
          </Link>
        ) : null}
        <Link className="home__areaLink" to={`/plans/${planId}/files`}>
          Files<span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );

  if (nothingStarted) {
    return (
      <section className="page home">
        <div>
          <div className="home__eyebrow">Private to you</div>
          <h2 className="page__title home__title">
            Make the important decisions clearer.
          </h2>
          <p className="home__lede">
            Think through your future care, what happens after your death, and the people
            who matter to you.
          </p>
        </div>
        <ul className="home__doors">
          {isCreator ? (
            <li className="home__card home__door">
              <label className="home__cardTitle" htmlFor="home-concern">
                What’s on your mind?
              </label>
              <textarea
                id="home-concern"
                className="home__box"
                rows={3}
                value={text}
                placeholder="E.g. I’m worried about what happens to our house when I’m gone..."
                disabled={waiting}
                onChange={(event) => setText(event.target.value)}
              />
              <button
                type="button"
                className="button button--primary home__action"
                disabled={waiting || text.trim() === '' || ownPlanThread === null}
                onClick={talkItThrough}
              >
                Talk it through →
              </button>
              {!sent ? (
                <span className="home__cardFacts">
                  You can start with something specific or just tell Mor what’s happening
                  in your life.
                </span>
              ) : (
                <FacilitatorState
                  facilitatorBusy={snapshot.thread?.facilitatorBusy === true}
                />
              )}
              {shownProblem === null ? null : (
                <p className="page__problem" role="alert">
                  {shownProblem}
                </p>
              )}
            </li>
          ) : null}
          <li className="home__card home__door">
            <span className="home__cardTitle">Explore what you can plan for</span>
            <span className="home__cardBody">
              Browse the full library, organized into four planning areas.
            </span>
            <Link
              className="home__talkLink home__doorLink"
              to={`/plans/${planId}/topics`}
            >
              Browse planning topics →
            </Link>
          </li>
          {isCreator ? (
            <li className="home__card home__door">
              <span className="home__cardTitle">Want Mor to guide you?</span>
              <span className="home__cardBody">
                Mor will ask about your circumstances and help identify what deserves
                attention.
              </span>
              <Link
                className="home__talkLink home__doorLink"
                to={`/plans/${planId}/start`}
              >
                Help me get started →
              </Link>
            </li>
          ) : null}
        </ul>
        {aboutPlan(principal)}
      </section>
    );
  }

  return (
    <section className="page home">
      <div>
        <div className="home__eyebrow">Welcome back</div>
        <h2 className="page__title home__title">Where things stand</h2>
        <p className="home__lede">
          Pick up where you left off, revisit a decision, or start something new.
        </p>
      </div>
      {featured === undefined ? null : (
        <section className="home__section">
          <h3 className="home__heading">Pick up where you left off</h3>
          <ul className="home__topics">
            {featured.kind === 'issue' ? (
              <li className="home__card">
                <div className="home__cardInfo">
                  <span className="home__cardTitle">{featured.issue.label}</span>
                  {featured.issue.intention === null ? null : (
                    <span className="home__cardBody">{featured.issue.intention}</span>
                  )}
                  {featured.issue.openQuestion === null ? null : (
                    <span className="home__cardFacts">
                      Next to explore: {featured.issue.openQuestion}
                    </span>
                  )}
                </div>
                <Link
                  className="button button--primary home__action"
                  to={`/plans/${planId}/topics/${featured.issue.topic}/issues/${featured.issue.id}`}
                >
                  Continue
                </Link>
              </li>
            ) : (
              <li className="home__card">
                <div className="home__cardInfo">
                  <span className="home__cardTitle">{phraseOf(featured.key)}</span>
                  <span className="home__cardBody">Exploring a topic</span>
                </div>
                <Link
                  className="button button--primary home__action"
                  to={`/plans/${planId}/topics/${featured.key}/issues`}
                >
                  Continue
                </Link>
              </li>
            )}
          </ul>
        </section>
      )}
      {otherIssues.length === 0 ? null : (
        <section className="home__section">
          <h3 className="home__heading">
            {featured === undefined ? 'Your issues' : 'Other issues'}
          </h3>
          <ul className="home__topics">
            {otherIssues.map((issue) => (
              <li key={issue.id} className="home__card">
                <div className="home__cardInfo">
                  <span className="home__cardTitle">{issue.label}</span>
                  <span className="home__cardFacts">{phraseOf(issue.topic)}</span>
                  {issue.intention === null ? null : (
                    <span className="home__cardBody">{issue.intention}</span>
                  )}
                </div>
                <Link
                  className="button button--outline home__action"
                  to={`/plans/${planId}/topics/${issue.topic}/issues/${issue.id}`}
                >
                  Open
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="home__areaLinks">
        {isCreator ? (
          <Link className="home__areaLink" to={`/plans/${planId}/start`}>
            Start another concern<span aria-hidden="true">→</span>
          </Link>
        ) : null}
        <Link className="home__areaLink" to={`/plans/${planId}/topics`}>
          Explore planning topics<span aria-hidden="true">→</span>
        </Link>
        {principal ? (
          <Link className="home__areaLink" to={`/plans/${planId}/people`}>
            People &amp; relationships<span aria-hidden="true">→</span>
          </Link>
        ) : null}
        <Link className="home__areaLink" to={`/plans/${planId}/files`}>
          Files<span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
